import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbCheck } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import {
  type EmailModifyAction,
  type EmailModifyResultPayload,
  parseEmailModifyResultPayload,
} from "./emailModifyResultSchema";

interface EmailModifyResultRendererProps {
  block: StreamingBlock;
}

const SUMMARY_KEY: Record<EmailModifyAction, { one: string; many: string }> = {
  archive: { one: "email_modify_result_card.archived_one", many: "email_modify_result_card.archived" },
  unarchive: { one: "email_modify_result_card.unarchived_one", many: "email_modify_result_card.unarchived" },
  mark_read: { one: "email_modify_result_card.marked_read_one", many: "email_modify_result_card.marked_read" },
  mark_unread: { one: "email_modify_result_card.marked_unread_one", many: "email_modify_result_card.marked_unread" },
  star: { one: "email_modify_result_card.starred_one", many: "email_modify_result_card.starred" },
  unstar: { one: "email_modify_result_card.unstarred_one", many: "email_modify_result_card.unstarred" },
  mark_important: {
    one: "email_modify_result_card.marked_important_one",
    many: "email_modify_result_card.marked_important",
  },
  mark_not_important: {
    one: "email_modify_result_card.marked_not_important_one",
    many: "email_modify_result_card.marked_not_important",
  },
  add_label: { one: "email_modify_result_card.labeled_one", many: "email_modify_result_card.labeled" },
  remove_label: { one: "email_modify_result_card.unlabeled_one", many: "email_modify_result_card.unlabeled" },
};

export const EmailModifyResultRenderer = (props: EmailModifyResultRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<EmailModifyResultPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseEmailModifyResultPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  const summary = (data: EmailModifyResultPayload): string => {
    const keys = SUMMARY_KEY[data.action];
    return data.count === 1 ? rt.t(keys.one) : rt.t(keys.many, { count: String(data.count) });
  };

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("email_modify_result_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="flex max-w-xl items-center gap-2 rounded-lg border border-primary-border bg-panel px-3 py-2">
            <TbCheck class="size-4 shrink-0 text-brand" />
            <span class="text-sm text-primary-text">{summary(data())}</span>
          </div>
        )}
      </Show>
    </Show>
  );
};
